import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import PublicFooter from "@/components/PublicFooter";
import { COMPANY, LEGAL_EFFECTIVE_DATE, LEGAL_VERSION } from "@/lib/legalVersion";

// Phase P.18 — GDPR — Imprint / Legal Notice. Covers the disclosure obligations
// commonly imposed on B2B websites operating in the EU (equivalents to §5 TMG
// in Germany, but also serves the Irish requirement to disclose the legal
// name of a business supplying online services under S.I. No. 68/2003).

const Line = ({ label, children, testid }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-1 sm:gap-4 py-2 border-b border-border/60" data-testid={testid}>
    <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="text-sm text-foreground/90">{children}</div>
  </div>
);

export default function Imprint() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="imprint-page">
      <div className="border-b border-border">
        <div className="max-w-[820px] mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm font-bold" data-testid="imprint-back-home">
            <ArrowLeft className="h-4 w-4" /> Back to JobsBook
          </Link>
          <span className="mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Legal</span>
        </div>
      </div>

      <main className="max-w-[820px] mx-auto px-5 md:px-8 py-12">
        <div className="mono text-[11px] uppercase tracking-[0.3em] text-accent">// {COMPANY.trading_name}</div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2">Imprint / Legal Notice</h1>
        <div className="mt-2 text-sm text-muted-foreground">
          Effective: <span className="mono">{LEGAL_EFFECTIVE_DATE}</span>{" "}
          · Version <span className="mono">{LEGAL_VERSION}</span>
        </div>

        <p className="mt-4 text-sm leading-relaxed text-foreground/80">
          Information provided in accordance with the Irish disclosure requirements for
          providers of online services and equivalent EU Member State obligations.
        </p>

        <section className="mt-8 border border-border rounded-md p-4 bg-card" data-testid="imprint-company">
          <h2 className="text-lg font-extrabold tracking-tight mb-2">Company</h2>
          <Line label="Legal name" testid="imprint-line-legal">{COMPANY.legal_name}</Line>
          <Line label="Trading as" testid="imprint-line-trading">{COMPANY.trading_name}</Line>
          <Line label="Registered address" testid="imprint-line-address">{COMPANY.address}</Line>
          <Line label="Country" testid="imprint-line-country">Ireland</Line>
          <Line label="Company registration" testid="imprint-line-crn">
            <span className="mono">CRO {COMPANY.cro_number}</span>
          </Line>
          <Line label="VAT number" testid="imprint-line-vat">
            <span className="mono">{COMPANY.vat_number}</span>
          </Line>
          <Line label="Directors" testid="imprint-line-directors">
            {COMPANY.directors}
          </Line>
        </section>

        <section className="mt-6 border border-border rounded-md p-4 bg-card" data-testid="imprint-contact">
          <h2 className="text-lg font-extrabold tracking-tight mb-2">Contact</h2>
          <Line label="Email" testid="imprint-line-email">
            <a href={`mailto:${COMPANY.contact_email}`} className="text-accent underline">{COMPANY.contact_email}</a>
          </Line>
          <Line label="Data protection" testid="imprint-line-dpo">
            <a href={`mailto:${COMPANY.contact_email}`} className="text-accent underline">{COMPANY.contact_email}</a>
            <span className="text-xs text-muted-foreground ml-2">(same mailbox — monitored by staff with GDPR responsibility)</span>
          </Line>
        </section>

        <section className="mt-6 border border-border rounded-md p-4 bg-card" data-testid="imprint-supervisory">
          <h2 className="text-lg font-extrabold tracking-tight mb-2">Supervisory authority</h2>
          <p className="text-sm text-foreground/85 leading-relaxed">
            {COMPANY.supervisory_authority}, 21 Fitzwilliam Square South, Dublin 2, D02 RD28,
            Ireland. Web:{" "}
            <a className="text-accent underline" href={COMPANY.supervisory_url} target="_blank" rel="noopener noreferrer">{COMPANY.supervisory_url}</a>
          </p>
        </section>

        <section className="mt-6 border border-border rounded-md p-4 bg-card" data-testid="imprint-online-dispute">
          <h2 className="text-lg font-extrabold tracking-tight mb-2">Online dispute resolution</h2>
          <p className="text-sm text-foreground/85 leading-relaxed">
            The European Commission provides an online dispute resolution platform for consumer
            disputes:{" "}
            <a className="text-accent underline" href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">ec.europa.eu/consumers/odr</a>.
            We are neither obliged nor willing to participate in dispute-resolution proceedings
            before a consumer arbitration board.
          </p>
        </section>

        <div className="mt-16 pt-8 border-t border-border">
          <PublicFooter tone="dark" />
          {/* Phase P.28 — Admin login relocated here from the hidden landing
              dot. Kept intentionally understated (small text link) so it
              doesn't compete with legal copy but is trivially findable via
              the site imprint. */}
          <div className="mt-6 text-center">
            <Link
              to="/admin/login"
              className="text-[9px] text-muted-foreground/50 hover:text-muted-foreground tracking-wide"
              data-testid="imprint-admin-login-link"
            >
              Admin login
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
